import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { GET_BIDDING_AUCTION, VEHICLE_LIST } from "../../constant";
import { GET } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { getAuth, getAuthUserId } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import { getDealerDisplayName } from "../../utils/dealerIdentity";
import {
  authHeaders,
  isApiSuccess,
  listFromPayload,
  officeId,
  pick,
  readJson,
  unwrapOffice,
} from "../../components/Offices/officeHelpers";
import {
  appointmentDate,
  appointmentId,
  appointmentTime,
  clientName,
  formatAppointmentTime,
  formatIsoDate,
  parseAppointmentsPage,
  vehicleTitle,
} from "../../components/OfficeAppointments/appointmentHelpers";
import { officeLabel } from "../../components/InspectionUsers/inspectionUserHelpers";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const GOLD = "#c4a052";
const NAVY = "#1d4ed8";
const SLATE = "#334155";
const TEAL = "#0f766e";
const ROSE = "#be123c";
const SKY = "#0284c7";

function asList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  return [];
}

function listingType(item) {
  const hasAuction = Array.isArray(item?.auction) && item.auction.length > 0;
  if (hasAuction) return "auction";
  if (item?.buy_now) return "buynow";
  return "unlisted";
}

function countFrom(auction, listKey, countKey) {
  const n = Number(auction?.[countKey]);
  if (Number.isFinite(n) && n >= 0 && auction?.[countKey] != null) return n;
  return asList(auction?.[listKey]).length;
}

function toDayKey(value) {
  if (value == null || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function pickDate(item, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    if (item?.[keys[i]]) return item[keys[i]];
  }
  return null;
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: { boxWidth: 10, font: { size: 11 } },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: {
      beginAtZero: true,
      ticks: { precision: 0, font: { size: 11 } },
      grid: { color: "rgba(15, 23, 42, 0.06)" },
    },
  },
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: { boxWidth: 10, font: { size: 11 } },
    },
  },
};

function appointmentDayKey(item) {
  const raw = appointmentDate(item);
  if (!raw || raw === "—") return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(String(raw))) {
    return String(raw).slice(0, 10);
  }
  return toDayKey(raw);
}

function isUpcomingAppointment(item, todayKey) {
  const day = appointmentDayKey(item);
  if (!day) return false;
  return day >= todayKey;
}

function DashboardHome({
  auth,
  auction,
  auctionLoading,
  vehicles,
  vehiclesLoading,
  getBiddingAuction,
  getVehicles,
}) {
  const dealerName = getDealerDisplayName(auth);
  const userId = auth?.id || getAuthUserId();
  const [offices, setOffices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      getBiddingAuction(userId);
      getVehicles(userId);
    }
  }, [userId]);

  useEffect(() => {
    const token = getAuth()?.token;
    if (!token) {
      setOffices([]);
      setAppointments([]);
      setOfficesLoading(false);
      setAppointmentsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setOfficesLoading(true);
      setAppointmentsLoading(true);
      try {
        const response = await fetch(API_ENDPOINT(Labels.office_listing), {
          headers: authHeaders(token),
        });
        const json = await readJson(response);
        if (!isApiSuccess(response, json)) throw new Error("list failed");
        const officeRows = listFromPayload(json)
          .map((office) => unwrapOffice(office))
          .filter((office) => officeId(office));
        if (cancelled) return;
        setOffices(officeRows);
        setOfficesLoading(false);

        const start = new Date();
        start.setDate(start.getDate() - 30);
        const end = new Date();
        end.setDate(end.getDate() + 30);
        const params = new URLSearchParams({
          page: "1",
          page_size: "100",
          starting_date: formatIsoDate(start),
          ending_date: formatIsoDate(end),
        });

        const batches = await Promise.all(
          officeRows.map(async (office) => {
            const id = officeId(office);
            try {
              const apptRes = await fetch(
                `${API_ENDPOINT(
                  Labels.office_appointments_listing
                )}${id}/?${params.toString()}`,
                { headers: authHeaders(token) }
              );
              const apptJson = await readJson(apptRes);
              if (!isApiSuccess(apptRes, apptJson)) return [];
              return parseAppointmentsPage(apptJson).items.map((item) => ({
                ...item,
                __office: office,
                __officeId: id,
              }));
            } catch {
              return [];
            }
          })
        );
        if (!cancelled) {
          setAppointments(batches.flat());
        }
      } catch {
        if (!cancelled) {
          setOffices([]);
          setAppointments([]);
        }
      } finally {
        if (!cancelled) {
          setOfficesLoading(false);
          setAppointmentsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const bidCounts = useMemo(
    () => ({
      active: countFrom(auction, "active_auction", "active_auction_count"),
      upcoming: countFrom(auction, "upcoming_auction", "upcoming_auction_count"),
      won: countFrom(auction, "won_auction", "won_auction_count"),
      lost: countFrom(auction, "lost_auction", "lost_auction_count"),
    }),
    [auction]
  );

  const vehicleRows = useMemo(() => asList(vehicles), [vehicles]);
  const vehicleMix = useMemo(() => {
    const mix = { auction: 0, buynow: 0, unlisted: 0 };
    vehicleRows.forEach((item) => {
      mix[listingType(item)] += 1;
    });
    return mix;
  }, [vehicleRows]);

  const todayKey = formatIsoDate(new Date());

  const appointmentStats = useMemo(() => {
    const byOfficeMap = new Map();
    offices.forEach((office) => {
      const id = String(officeId(office));
      byOfficeMap.set(id, {
        id,
        label: officeLabel(office) || pick(office, ["Address", "address"], "Office"),
        count: 0,
      });
    });

    let upcoming = 0;
    let past = 0;
    appointments.forEach((item) => {
      const oid = String(item.__officeId || officeId(item.__office) || "");
      if (oid && byOfficeMap.has(oid)) {
        byOfficeMap.get(oid).count += 1;
      } else if (oid) {
        byOfficeMap.set(oid, {
          id: oid,
          label:
            officeLabel(item.__office) ||
            pick(item.__office, ["Address", "address"], "Office"),
          count: 1,
        });
      }
      if (isUpcomingAppointment(item, todayKey)) upcoming += 1;
      else past += 1;
    });

    const byOffice = Array.from(byOfficeMap.values()).sort(
      (a, b) => b.count - a.count
    );

    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      days.push(formatIsoDate(d));
    }
    const buckets = Object.fromEntries(days.map((day) => [day, 0]));
    appointments.forEach((item) => {
      const key = appointmentDayKey(item);
      if (key && buckets[key] != null) buckets[key] += 1;
    });

    const upcomingList = appointments
      .filter((item) => isUpcomingAppointment(item, todayKey))
      .sort((a, b) => {
        const da = `${appointmentDayKey(a) || ""}T${appointmentTime(a) || ""}`;
        const db = `${appointmentDayKey(b) || ""}T${appointmentTime(b) || ""}`;
        return da.localeCompare(db);
      });

    return {
      total: appointments.length,
      upcoming,
      past,
      byOffice,
      trend: {
        labels: days.map((day) => day.slice(5)),
        values: days.map((day) => buckets[day]),
      },
      upcomingList,
    };
  }, [appointments, offices, todayKey]);

  const bidTrend = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      days.push(d.toISOString().slice(0, 10));
    }
    const buckets = Object.fromEntries(days.map((day) => [day, 0]));
    const dateKeys = [
      "bidOffer",
      "bid_on",
      "bid_date",
      "boughtOn",
      "bought_on",
      "purchase_date",
      "startIn",
      "start_in",
      "start_date",
      "created_at",
    ];
    []
      .concat(
        asList(auction?.active_auction),
        asList(auction?.upcoming_auction),
        asList(auction?.won_auction),
        asList(auction?.lost_auction)
      )
      .forEach((item) => {
        const key = toDayKey(pickDate(item, dateKeys));
        if (key && buckets[key] != null) buckets[key] += 1;
      });
    return {
      labels: days.map((day) => day.slice(5)),
      values: days.map((day) => buckets[day]),
    };
  }, [auction]);

  const bidTotal =
    bidCounts.active + bidCounts.upcoming + bidCounts.won + bidCounts.lost;
  const loading =
    (auctionLoading && bidTotal === 0) ||
    (vehiclesLoading && vehicleRows.length === 0) ||
    officesLoading ||
    appointmentsLoading;

  const bidBarData = {
    labels: ["Active", "Upcoming", "Won", "Lost"],
    datasets: [
      {
        label: "Bids",
        data: [
          bidCounts.active,
          bidCounts.upcoming,
          bidCounts.won,
          bidCounts.lost,
        ],
        backgroundColor: [NAVY, GOLD, TEAL, ROSE],
        borderRadius: 6,
        maxBarThickness: 42,
      },
    ],
  };

  const vehicleDoughnut = {
    labels: ["Auction", "Buy now", "Unlisted"],
    datasets: [
      {
        data: [vehicleMix.auction, vehicleMix.buynow, vehicleMix.unlisted],
        backgroundColor: [NAVY, GOLD, SLATE],
        borderWidth: 0,
      },
    ],
  };

  const officeBarData = {
    labels: appointmentStats.byOffice.map((row) => {
      const name = String(row.label || "Office");
      return name.length > 18 ? `${name.slice(0, 16)}…` : name;
    }),
    datasets: [
      {
        label: "Appointments",
        data: appointmentStats.byOffice.map((row) => row.count),
        backgroundColor: SKY,
        borderRadius: 6,
        maxBarThickness: 36,
      },
    ],
  };

  const bookingStatusDoughnut = {
    labels: ["Upcoming", "Past"],
    datasets: [
      {
        data: [appointmentStats.upcoming, appointmentStats.past],
        backgroundColor: [TEAL, "rgba(15, 23, 42, 0.18)"],
        borderWidth: 0,
      },
    ],
  };

  const trendData = {
    labels: appointmentStats.trend.labels,
    datasets: [
      {
        label: "Appointments (14 days)",
        data: appointmentStats.trend.values,
        borderColor: SKY,
        backgroundColor: "rgba(2, 132, 199, 0.14)",
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };

  const bidTrendData = {
    labels: bidTrend.labels,
    datasets: [
      {
        label: "Bid records (14 days)",
        data: bidTrend.values,
        borderColor: NAVY,
        backgroundColor: "rgba(29, 78, 216, 0.14)",
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };

  const kpis = [
    {
      label: "Active bids",
      value: bidCounts.active,
      to: "/dealers/dashbord/mybids",
    },
    {
      label: "Appointments",
      value: appointmentStats.total,
      to: "/dealers/dashbord/appointments",
    },
    {
      label: "Vehicles",
      value: vehicleRows.length,
      to: "/dealers/dashbord/myvehicles",
    },
    {
      label: "Offices",
      value: offices.length,
      to: "/dealers/dashbord/offices",
    },
  ];

  return (
    <div className="dd-home">
      <p className="dd-home__hello">
        Welcome back{dealerName && dealerName !== "Dealer" ? `, ${dealerName}` : ""}.
        This overview uses your bids, vehicle listings, and office appointments.
      </p>

      {loading ? <p className="dd-home__status">Loading workspace figures…</p> : null}

      <div className="dd-home__kpis">
        {kpis.map((item) => (
          <Link key={item.label} to={item.to} className="dd-home__kpi">
            <span className="dd-home__kpi-label">{item.label}</span>
            <span className="dd-home__kpi-value">{item.value}</span>
          </Link>
        ))}
      </div>

      <div className="dd-home__grid">
        <section className="dd-home__card">
          <h2>Bid pipeline</h2>
          <p>Active, upcoming, won, and lost lots from your bid desk.</p>
          <div className="dd-home__chart">
            {bidTotal ? (
              <Bar data={bidBarData} options={chartOptions} />
            ) : (
              <p className="dd-home__empty">No bid records yet.</p>
            )}
          </div>
        </section>

        <section className="dd-home__card">
          <h2>Vehicle mix</h2>
          <p>How your listed stock is currently offered.</p>
          <div className="dd-home__chart">
            {vehicleRows.length ? (
              <Doughnut data={vehicleDoughnut} options={doughnutOptions} />
            ) : (
              <p className="dd-home__empty">No vehicles on the desk yet.</p>
            )}
          </div>
        </section>

        <section className="dd-home__card dd-home__card--wide">
          <h2>Bid activity</h2>
          <p>Dated bid records that fall in the last 14 days.</p>
          <div className="dd-home__chart dd-home__chart--wide">
            {bidTrend.values.some((n) => n > 0) ? (
              <Line data={bidTrendData} options={chartOptions} />
            ) : (
              <p className="dd-home__empty">
                No dated bid activity in the last two weeks.
              </p>
            )}
          </div>
        </section>

        <section className="dd-home__card">
          <div className="dd-home__card-head">
            <div>
              <h2>Bookings by office</h2>
              <p>Appointment counts per inspection centre (±30 days).</p>
            </div>
            <Link to="/dealers/dashbord/offices" className="dd-btn dd-btn--ghost">
              View offices
            </Link>
          </div>
          <div className="dd-home__chart">
            {appointmentStats.byOffice.some((row) => row.count > 0) ? (
              <Bar data={officeBarData} options={chartOptions} />
            ) : offices.length ? (
              <p className="dd-home__empty">No appointments in this period.</p>
            ) : (
              <p className="dd-home__empty">No offices listed yet.</p>
            )}
          </div>
        </section>

        <section className="dd-home__card">
          <h2>Booking status</h2>
          <p>Upcoming vs past appointments in the current window.</p>
          <div className="dd-home__chart">
            {appointmentStats.total ? (
              <Doughnut data={bookingStatusDoughnut} options={doughnutOptions} />
            ) : (
              <p className="dd-home__empty">No appointments to chart yet.</p>
            )}
          </div>
        </section>

        <section className="dd-home__card dd-home__card--wide">
          <div className="dd-home__card-head">
            <div>
              <h2>Appointment activity</h2>
              <p>Office bookings dated in the last 14 days.</p>
            </div>
            <Link
              to="/dealers/dashbord/appointments"
              className="dd-btn dd-btn--ghost"
            >
              View appointments
            </Link>
          </div>
          <div className="dd-home__chart dd-home__chart--wide">
            {appointmentStats.trend.values.some((n) => n > 0) ? (
              <Line data={trendData} options={chartOptions} />
            ) : (
              <p className="dd-home__empty">
                No dated appointments in the last two weeks.
              </p>
            )}
          </div>
        </section>

        <section className="dd-home__card dd-home__card--wide">
          <div className="dd-home__card-head">
            <div>
              <h2>Next appointments</h2>
              <p>Upcoming inspections across all offices.</p>
            </div>
            <Link
              to="/dealers/dashbord/appointments"
              className="dd-btn dd-btn--ghost"
            >
              View all
            </Link>
          </div>
          {appointmentStats.upcomingList.length ? (
            <ul className="dd-home__appointments">
              {appointmentStats.upcomingList.slice(0, 8).map((item, index) => {
                const id = item.__officeId;
                const apptKey = appointmentId(item) || `up-${index}`;
                return (
                  <li key={String(apptKey)}>
                    <Link
                      to={
                        id
                          ? `/dealers/dashbord/offices/${id}/appointments`
                          : "/dealers/dashbord/appointments"
                      }
                    >
                      <span>
                        {clientName(item)} · {vehicleTitle(item)}
                        <em className="dd-home__appt-office">
                          {officeLabel(item.__office) ||
                            pick(item.__office, ["Address", "address"], "Office")}
                        </em>
                      </span>
                      <strong>
                        {appointmentDate(item)}
                        {appointmentTime(item)
                          ? ` · ${formatAppointmentTime(appointmentTime(item))}`
                          : ""}
                      </strong>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="dd-home__empty">No upcoming office appointments.</p>
          )}
        </section>
      </div>
    </div>
  );
}
const mapStateToProps = (state) => ({
  auth: state.auth,
  auction: state.profile.auction,
  auctionLoading: state.profile.auctionLoading,
  vehicles: state.myVehicles?.data,
  vehiclesLoading: state.myVehicles?.loading,
});

const mapDispatchToProps = (dispatch) => ({
  getBiddingAuction: (id) =>
    dispatch({
      type: GET_BIDDING_AUCTION,
      payload: {
        type: GET_BIDDING_AUCTION,
        label: `${Labels.get_bidding_auction}${id}`,
        method: GET,
        hideOverlayLoader: true,
      },
    }),
  getVehicles: (id) =>
    dispatch({
      type: VEHICLE_LIST,
      payload: {
        type: VEHICLE_LIST,
        label: `${Labels.vechiellist}${id}/`,
        method: GET,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardHome);
